import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import styles from './ProjectDocumentationNew.module.scss';
import NewNavImproved from '../../components/Nav/NewNavImproved.tsx';
import BotoneraModern from '../../components/Botonera/BotoneraModern.tsx';
import useNavVisibility from '../../hooks/useNavVisibility.ts';
import CreationProgressModal from '../../components/UI/CreationProgressModal/CreationProgressModal.tsx';
import environment from '../../environments/index';
import {
    fetchCategories,
    fetchUses,
    fetchMaterialities,
    fetchFileTags,
    fetchSubCategories,
    setSelectedType,
    setSelectedCategory,
    setSelectedSubCategory,
    setSelectedUses,
    setSelectedMaterialities,
    setFormData,
    createProjectDocumentation
} from '../../store/actions/projectDocumentationActions';
import { ProjectDocumentationForm, FileItem } from '../../types/projectDocumentation';
import { DEFAULT_FORM_VALUES } from '../../constants/projectDocumentation.ts';
import { buildProjectDocumentationPayload } from '../../utils/projectDocumentationBuilder.ts';
import { buildProfileUrl } from '../../utils/navigationHelpers.ts';
import TypeSelector from './components/TypeSelector.tsx';
import CategorySelector from './components/CategorySelector.tsx';
import SubCategorySelector from './components/SubCategorySelector.tsx';
import UseSelector from './components/UseSelector.tsx';
import MaterialitySelector from './components/MaterialitySelector.tsx';
import ProjectForm from './components/ProjectForm.tsx';
import Breadcrumbs from '../../components/UI/Breadcrumbs/Breadcrumbs.tsx';

interface ProjectDocumentationNewProps {
    categories: any[];
    subCategories: any[];
    uses: any[];
    materialities: any[];
    fileTags: any[];
    selectedType: 'proyecto' | 'anteproyecto' | null;
    selectedCategory: string | null;
    selectedSubCategory: string | null;
    selectedUses: string[];
    selectedMaterialities: string[];
    formData: ProjectDocumentationForm;
    loading: boolean;
    createLoading: boolean;
    userData: any;
    fetchCategories: () => void;
    fetchUses: () => void;
    fetchMaterialities: () => void;
    fetchFileTags: () => void;
    fetchSubCategories: (categoryId: string) => void;
    setSelectedType: (type: 'proyecto' | 'anteproyecto') => void;
    setSelectedCategory: (categoryId: string) => void;
    setSelectedSubCategory: (subCategoryId: string) => void;
    setSelectedUses: (useIds: string[]) => void;
    setSelectedMaterialities: (materialityIds: string[]) => void;
    setFormData: (formData: Partial<ProjectDocumentationForm>) => void;
    createProjectDocumentation: (payload: any, items?: any[], onProgress?: (current: number, total: number) => void) => Promise<any>;
}

const ProjectDocumentationNew: React.FC<ProjectDocumentationNewProps> = ({
    categories,
    subCategories,
    uses,
    materialities,
    fileTags,
    selectedType,
    selectedCategory,
    selectedSubCategory,
    selectedUses,
    selectedMaterialities,
    formData,
    loading,
    createLoading,
    userData,
    fetchCategories,
    fetchUses,
    fetchMaterialities,
    fetchFileTags,
    fetchSubCategories,
    setSelectedType,
    setSelectedCategory,
    setSelectedSubCategory,
    setSelectedUses,
    setSelectedMaterialities,
    setFormData,
    createProjectDocumentation
}) => {
    const navigate = useNavigate();
    const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();
    const [currentStep, setCurrentStep] = useState(1);
    const [creationModalState, setCreationModalState] = useState<'creating' | 'uploading' | 'success' | 'pending' | 'error' | null>(null);
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
    const [projectId, setProjectId] = useState<string | null>(null);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                setSelectedType(null);
                setSelectedCategory(null);
                setSelectedSubCategory(null);
                setSelectedUses([]);
                setSelectedMaterialities([]);
                setFormData(DEFAULT_FORM_VALUES);

                await Promise.all([
                    fetchCategories(),
                    fetchUses(),
                    fetchMaterialities(),
                    fetchFileTags()
                ]);
            } catch (error) {
                console.error('Error loading initial data:', error);
            }
        };

        loadInitialData();
    }, [fetchCategories, fetchUses, fetchMaterialities, fetchFileTags, setFormData, setSelectedType, setSelectedCategory, setSelectedSubCategory, setSelectedUses, setSelectedMaterialities]);

    // Cargar subcategorías cuando se selecciona una categoría
    useEffect(() => {
        if (selectedCategory) {
            fetchSubCategories(selectedCategory);
        }
    }, [selectedCategory, fetchSubCategories]);

    const handleTypeSelect = (type: 'proyecto' | 'anteproyecto') => {
        setSelectedType(type);
        setCurrentStep(2);
    };

    const handleCategorySelect = (categoryId: string) => {
        setSelectedCategory(categoryId);
        fetchSubCategories(categoryId);
        setCurrentStep(3);
    };

    const handleSubCategorySelect = (subCategoryId: string) => {
        setSelectedSubCategory(subCategoryId);
        setCurrentStep(4);
    };

    const handleUsesSelect = (useId: string) => {
        setSelectedUses([useId]);
        setCurrentStep(5);
    };

    const handleMaterialitiesSelect = (materialityId: string) => {
        setSelectedMaterialities([materialityId]);
        setCurrentStep(6);
    };

    const handleFormSubmit = async (finalFormData: ProjectDocumentationForm, fileItems: FileItem[]) => {
        try {
            setCreationModalState('creating');
            
            const payload = buildProjectDocumentationPayload(
                finalFormData,
                {
                    selectedType: selectedType || 'anteproyecto',
                    selectedSubCategory: selectedSubCategory || '',
                    selectedMaterialities,
                    selectedUses
                },
                false,
                userData?.id
            );

            const handleUploadProgress = (current: number, total: number) => {
                setCreationModalState('uploading');
                setUploadProgress({ current, total });
            };

            const result = await createProjectDocumentation(payload, fileItems, handleUploadProgress);
            const createdProjectId = result?.data?.id;
            
            if (createdProjectId) {
                setProjectId(createdProjectId);
                setCreationModalState('pending');
            } else {
                setCreationModalState('success');
            }
        } catch (error: any) {
            console.error('Error al crear anteproyecto:', error);
            setCreationModalState('error');
        }
    };

    const handleModalSuccess = () => {
        setCreationModalState(null);
        setUploadProgress({ current: 0, total: 0 });
        setProjectId(null);
        if (userData) {
            const userName = userData.type === "COMPANY" 
                ? userData.razon_social 
                : `${userData.name} ${userData.last_name}`;
            const profileUrl = buildProfileUrl(String(userData.id), userName, 'Documentaciones de obra');
            navigate(profileUrl);
        } else {
            navigate('/dashboard');
        }
    };

    const handleModalError = () => {
        setCreationModalState(null);
        setUploadProgress({ current: 0, total: 0 });
        setProjectId(null);
    };

    const handleViewPreview = () => {
        if (projectId) {
            const marketplaceUrl = environment.marketplace.url;
            const previewUrl = `${marketplaceUrl}/proyecto/${projectId}`;
            window.open(previewUrl, '_blank');
        }
    };


    if (loading && categories.length === 0) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p>Cargando...</p>
            </div>
        );
    }


    return (
        <div className={styles.container}>
            {!ocultarEnMobile && <NewNavImproved />}
            {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
            <div className={styles.mainContainer}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <Breadcrumbs />
            <h1 className={styles.title}>
                            Crear{' '}
                            <span style={{ color: "#234ba2" }}>
                                {selectedType === 'proyecto' ? 'Proyecto' : selectedType === 'anteproyecto' ? 'Ante Proyecto' : 'Documentación'}
                            </span>
                            {" "}de Obra
                        </h1>
                        <div className={styles.stepsContainer}>
                            {[
                                { step: 1, name: 'Tipo' },
                                { step: 2, name: 'Categoría' },
                                { step: 3, name: 'Subcategoría' },
                                { step: 4, name: 'Uso' },
                                { step: 5, name: 'Materialidad' },
                                { step: 6, name: 'Detalles' }
                            ].map((stepInfo, index) => {
                                const isCompleted = currentStep > stepInfo.step;
                                const isActive = currentStep === stepInfo.step;
                                const isClickable = isCompleted;

                                return (
                                    <div 
                                        key={stepInfo.step} 
                                        className={`${styles.stepItem} ${isClickable ? styles.clickable : ''}`}
                                        onClick={() => isClickable && setCurrentStep(stepInfo.step)}
                                    >
                                        <div className={`${styles.stepCircle} ${
                                            isActive ? styles.active :
                                            isCompleted ? styles.completed : 
                                            styles.pending
                                        }`}>
                                            {isCompleted ? '✓' : stepInfo.step}
                                        </div>
                                        <div className={styles.stepInfo}>
                                            <div className={styles.stepName}>{stepInfo.name}</div>
                                        </div>
                                        {index < 5 && (
                                            <div className={`${styles.stepConnector} ${isCompleted ? styles.completed : ''}`} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className={styles.content}>
                    {currentStep === 1 && (
                        <TypeSelector
                            selectedType={selectedType}
                            onSelect={handleTypeSelect}
                        />
                    )}

                    {currentStep === 2 && (
                        <CategorySelector
                            categories={categories}
                            selectedCategory={selectedCategory}
                            onSelect={handleCategorySelect}
                        />
                    )}

                    {currentStep === 3 && (
                        <SubCategorySelector
                            subCategories={subCategories}
                            selectedSubCategory={selectedSubCategory}
                            onSelect={handleSubCategorySelect}
                            onBack={() => setCurrentStep(2)}
                            loading={loading}
                        />
                    )}

                    {currentStep === 4 && (
                        <UseSelector
                            uses={uses}
                            selectedUse={selectedUses[0] || null}
                            onSelect={(useId: string) => handleUsesSelect(useId)}
                            onBack={() => setCurrentStep(3)}
                        />
                    )}

                    {currentStep === 5 && (
                        <MaterialitySelector
                            materialities={materialities}
                            selectedMateriality={selectedMaterialities[0] || null}
                            onSelect={(materialityId: string) => handleMaterialitiesSelect(materialityId)}
                            onBack={() => setCurrentStep(4)}
                        />
                    )}

                    {currentStep === 6 && (
                        <ProjectForm
                            formData={formData}
                            fileTags={fileTags}
                            projectType={selectedType || 'anteproyecto'}
                            onSubmit={handleFormSubmit}
                            onBack={() => setCurrentStep(5)}
                            loading={createLoading}
                            selectedSubCategory={selectedSubCategory}
                            selectedMaterialities={selectedMaterialities}
                            selectedUses={selectedUses}
                        />
                    )}
                </div>
            </div>

            {/* Modal de progreso de creación */}
            <CreationProgressModal
                isOpen={creationModalState !== null}
                state={creationModalState || 'creating'}
                message={
                    creationModalState === 'creating' 
                        ? `Guardando información del ${selectedType === 'proyecto' ? 'proyecto' : 'anteproyecto'}...`
                        : creationModalState === 'uploading'
                        ? 'Subiendo documentos...'
                        : creationModalState === 'pending'
                        ? `Tu ${selectedType === 'proyecto' ? 'proyecto' : 'anteproyecto'} ha sido creado exitosamente y está pendiente de aprobación por nuestro equipo. Una vez aprobado, será visible públicamente en el marketplace.`
                        : `No pudimos crear el ${selectedType === 'proyecto' ? 'proyecto' : 'anteproyecto'}. Por favor, verifica los datos e intenta nuevamente.`
                }
                progress={creationModalState === 'uploading' ? uploadProgress : undefined}
                projectId={projectId || undefined}
                projectPreviewUrl={projectId ? `${environment.marketplace.url}/proyecto/${projectId}` : undefined}
                onSuccess={handleModalSuccess}
                onError={handleModalError}
                onViewPreview={handleViewPreview}
            />
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    categories: state.anteProyecto.categories,
    subCategories: state.anteProyecto.subCategories,
    uses: state.anteProyecto.uses,
    materialities: state.anteProyecto.materialities,
    fileTags: state.anteProyecto.fileTags,
    selectedType: state.anteProyecto.selectedType,
    selectedCategory: state.anteProyecto.selectedCategory,
    selectedSubCategory: state.anteProyecto.selectedSubCategory,
    selectedUses: state.anteProyecto.selectedUses,
    selectedMaterialities: state.anteProyecto.selectedMaterialities,
    formData: state.anteProyecto.formData,
    loading: state.anteProyecto.loading,
    createLoading: state.anteProyecto.createLoading,
    userData: state.general.userData
});

export default connect(mapStateToProps, {
    fetchCategories,
    fetchUses,
    fetchMaterialities,
    fetchFileTags,
    fetchSubCategories,
    setSelectedType,
    setSelectedCategory,
    setSelectedSubCategory,
    setSelectedUses,
    setSelectedMaterialities,
    setFormData,
    createProjectDocumentation
})(ProjectDocumentationNew);
